import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGame } from "../hooks/useGameContext";
import { clearSession } from "../lib/storage";
import { RoomSettingsForm } from "../components/RoomSettingsForm";
import { RoomSettingsSummary } from "../components/RoomSettingsSummary";

export function LobbyPage() {
  const { room, game, playerId, emitUserAction, connected, leaveLobby } = useGame();
  const navigate = useNavigate();

  useEffect(() => {
    if (game && game.phase !== "LOBBY") navigate("/game");
  }, [game, navigate]);

  if (!room) {
    return (
      <div className="text-center py-20 space-y-4">
        <p className="text-yellow-400">{connected ? "Connexion au salon…" : "Connexion au serveur…"}</p>
        <Link to="/" className="text-violet-400 underline" onClick={() => clearSession()}>
          Retour à l&apos;accueil
        </Link>
      </div>
    );
  }

  const isHost = playerId === room.hostId;
  const me = room.players.find((p) => p.id === playerId);
  const connectedPlayers = room.players.filter((p) => p.connected);
  const playingConnected = connectedPlayers.filter((p) => p.role === "player");
  const spectatorConnected = connectedPlayers.filter((p) => p.role === "spectator");
  const allReady = playingConnected.length >= 1 && playingConnected.every((p) => p.isReady);
  const canStartGame = isHost && allReady && (room.settings.gameKind !== "bingo" || room.bingoValidation?.ok !== false);
  const isSoloLobby = playingConnected.length === 1;
  const inviteUrl = `${window.location.origin}/join?code=${room.code}`;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="cw-panel p-8 text-center">
        <p className="text-sm text-violet-400 uppercase tracking-widest">Code du salon</p>
        <p className="cw-title text-6xl font-extrabold tracking-[0.3em] text-orange-400 mt-2">{room.code}</p>
        <button type="button" className="cw-btn cw-btn-secondary mt-4 text-sm"
          onClick={() => navigator.clipboard.writeText(inviteUrl)}>
          Copier le lien d&apos;invitation
        </button>
      </div>

      <div className="cw-panel p-6">
        <h2 className="cw-title text-xl font-bold mb-4">
          Joueurs ({playingConnected.length}/{room.settings.maxPlayers})
        </h2>
        {spectatorConnected.length > 0 && (
          <p className="text-xs text-violet-400/70 mb-3">
            {spectatorConnected.length} spectateur{spectatorConnected.length > 1 ? "s" : ""} (hors quota)
          </p>
        )}
        <ul className="space-y-2">
          {room.players.map((p) => (
            <li key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <span>
                {p.nickname}
                {p.isHost && <span className="ml-2 text-xs text-orange-400">Hôte</span>}
                {p.role === "spectator" && <span className="ml-2 text-xs text-violet-400">Spectateur</span>}
                {!p.connected && <span className="ml-2 text-xs text-red-400">Déconnecté</span>}
              </span>
              <div className="flex items-center gap-3">
                {isHost && !p.isHost && p.role === "player" && (
                  <button
                    type="button"
                    className="text-xs text-red-400 hover:text-red-300 underline"
                    onClick={() => {
                      if (window.confirm(`Expulser ${p.nickname} du salon ?`)) {
                        emitUserAction("host:kick-player", { playerId: p.id });
                      }
                    }}
                  >
                    Expulser
                  </button>
                )}
                <span className={p.role === "spectator" ? "text-violet-400" : p.isReady ? "text-green-400" : "text-violet-400/50"}>
                  {p.role === "spectator" ? "Observe" : p.isReady ? "Prêt" : "En attente"}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="cw-panel p-6">
        <h2 className="cw-title text-xl font-bold mb-4">Options du salon</h2>
        {isHost ? (
          <>
            <RoomSettingsForm
              settings={room.settings}
              connectedPlayerCount={playingConnected.length}
              bingoValidation={room.bingoValidation}
              onChange={(patch) => emitUserAction("host:update-settings", { settings: patch })}
            />
            {playingConnected.length > 1 && (
              <p className="text-xs text-violet-400/60 mt-4">
                Modifier les options remet les autres joueurs en attente de confirmation.
              </p>
            )}
          </>
        ) : (
          <RoomSettingsSummary settings={room.settings} />
        )}
      </div>

      <div className="flex gap-4 justify-center flex-wrap">
        {!isHost && me?.role === "player" && (
          <button
            type="button"
            className="cw-btn cw-btn-secondary"
            onClick={() => emitUserAction("player:set-ready", { ready: !me?.isReady })}
          >
            {me?.isReady ? "Je ne suis plus prêt" : "Je suis prêt"}
          </button>
        )}
        {!isHost && me?.role === "spectator" && (
          <p className="text-sm text-violet-300/70 self-center">Vous rejoindrez en spectateur</p>
        )}
        {isHost && (
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              className="cw-btn cw-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={!canStartGame}
              onClick={() => emitUserAction("game:start")}
            >
              Lancer la partie
            </button>
            {!allReady && playingConnected.length >= 1 && (
              <p className="text-sm text-violet-400/70">Tous les joueurs doivent être prêts</p>
            )}
            {isHost && room.settings.gameKind === "bingo" && room.bingoValidation && !room.bingoValidation.ok && (
              <p className="text-sm text-red-400">Corrigez les catégories Bingo avant de lancer</p>
            )}
            {isSoloLobby && allReady && (
              <p className="text-sm text-violet-400/70">Partie solo — lancez quand vous voulez, ou invitez des amis</p>
            )}
          </div>
        )}
        <button type="button" className="cw-btn cw-btn-secondary" onClick={leaveLobby}>
          Quitter le lobby
        </button>
      </div>
    </div>
  );
}
